/**
 * Zone Size Thresholds
 *
 * Zone "size" is never stored — `prod.zones.zone_size` is vestigial (always written as
 * 'small', never recomputed, never read). Every size shown in the UI is derived at render
 * time from the zone's live recall count, which is why the buckets live here rather than
 * in the database.
 *
 * Recall volume differs by roughly an order of magnitude between OEMs, so the cut-points
 * that make a zone "Large" are per-OEM. The ladder is not the same *height* for every OEM
 * either. Most OEMs top out at Medium — they have no Large or Extra Large tier, and
 * therefore no "split this zone" advisory, because at their volume an oversize zone is not
 * a thing an admin needs to act on. That is expressed by setting the upper cut-points to
 * UNREACHABLE rather than by branching.
 *
 * ── CHANGING ZONE SIZES ───────────────────────────────────────────────────────────────
 * To retune how many recalls make a zone Medium/Large/Extra Large, edit FORD_SCALE or
 * GM_SCALE below. To move an OEM onto a different ladder, change its value in OEM_SCALE.
 * Nothing else in the codebase holds these numbers — every badge, polygon colour, map chip
 * and advisory reads them from here.
 * ──────────────────────────────────────────────────────────────────────────────────────
 *
 * This module is the single source of truth. Before it existed the same 300/500/700
 * literals were copy-pasted across seven functions and a dozen inline comparisons, and
 * had already drifted apart.
 */

import { KNOWN_OEMS, OEM_BRANDS, type OEM } from '@vehix/portal/constants/oems';

export type ZoneSizeTier = 'small' | 'medium' | 'large' | 'extra-large';

/**
 * Who a size label is written for.
 *
 * 'internal' — admin surfaces. The full four-tier ladder; "Extra Large" is the cue to go
 *   split the zone (see isZoneTooLarge).
 * 'customer' — Presentation mode, which is a shareable artifact. The ladder caps at Large:
 *   a dealership reading a map we handed them has no action to take on "Extra Large", and
 *   it reads as a defect in the map rather than a fact about their territory.
 *
 * Only the display *name* collapses. getZoneSizeTier and isZoneTooLarge are deliberately
 * audience-blind, so every internal advisory still fires on the real tier.
 */
export type ZoneSizeAudience = 'internal' | 'customer';

/** Inclusive lower bound of each tier above Small. */
export interface ZoneSizeThresholds {
  medium: number;
  large: number;
  extraLarge: number;
}

/**
 * Cut-point for a tier an OEM never reaches. Every `>=` comparison against it is false, so
 * getZoneSizeTier caps at the rung below and isZoneTooLarge stays quiet — which is the whole
 * point: an OEM with no Large tier has no oversize advisory either.
 */
const UNREACHABLE = Number.POSITIVE_INFINITY;

/**
 * Four rungs, calibrated on Ford's recall volume: 0-299 Small, 300-499 Medium, 500-699
 * Large, 700+ Extra Large. Only the highest-volume OEMs run the full ladder.
 */
const FORD_SCALE: ZoneSizeThresholds = { medium: 300, large: 500, extraLarge: 700 };

/**
 * Two rungs, calibrated on GM's recall volume: 0-100 Small, 101+ Medium. No Large, no Extra
 * Large, and so no "split this zone" advisory. This is the default for every OEM that is not
 * explicitly on FORD_SCALE.
 */
const GM_SCALE: ZoneSizeThresholds = {
  medium: 101,
  large: UNREACHABLE,
  extraLarge: UNREACHABLE
};

/**
 * The ladder every OEM runs on.
 *
 * Typed as Record<OEM, ...> against KNOWN_OEMS, so adding an OEM to the roster without giving
 * it a scale here is a compile error — not a silently wrong size on the map. That is the whole
 * reason this map lists OEMs already covered by DEFAULT_SCALE rather than only the exceptions.
 */
const OEM_SCALE: Record<OEM, ZoneSizeThresholds> = {
  // High recall volume — the full four-rung ladder.
  Ford: FORD_SCALE,
  Stellantis: FORD_SCALE,
  Hyundai: FORD_SCALE, // Genesis resolves here too, via OEM_BRANDS
  Kia: FORD_SCALE,

  // Everything else tops out at Medium.
  'General Motors': GM_SCALE,
  Toyota: GM_SCALE,
  Honda: GM_SCALE,
  Nissan: GM_SCALE,
  Volkswagen: GM_SCALE,
  BMW: GM_SCALE,
  'Mercedes-Benz': GM_SCALE,
  Audi: GM_SCALE,
  Porsche: GM_SCALE,
  Volvo: GM_SCALE,
  Polestar: GM_SCALE,
  Subaru: GM_SCALE,
  Mazda: GM_SCALE,
  Mitsubishi: GM_SCALE,
  'Land Rover': GM_SCALE,
  Jaguar: GM_SCALE,
  Tesla: GM_SCALE,
  Rivian: GM_SCALE,
  Lucid: GM_SCALE,
  VinFast: GM_SCALE,
  Other: GM_SCALE
};

/** Used for any OEM string that does not resolve to a roster entry. */
const DEFAULT_SCALE = GM_SCALE;

/**
 * Collapses the spelling variants that reach this module onto one lookup key.
 *
 * `prod.zones.oem` is a free-text VARCHAR with no FK or CHECK constraint, and three
 * vocabularies coexist upstream: display names in prod.zones ('General Motors'), display
 * names in prod.oem_brands ('Land Rover'), and lowercase slugs from the recall ingest
 * pipeline ('gm', 'land-rover'). Stripping separators as well as case is what makes
 * 'Land Rover' and 'land-rover' the same key.
 */
function normalizeOemKey(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[\s._-]+/g, '');
}

/**
 * Short forms that are neither a roster name nor a brand, so nothing else would resolve them.
 * 'GM' is emitted by scheduleNameParser.ts and the ingest pipeline, while zones, tenants and
 * the dropdowns all store 'General Motors' — this alias is what bridges the two. (schemas/oem.ts
 * used to spell it 'GM' too; it now derives straight from KNOWN_OEMS. 'vw' needs no entry — it
 * is already a Volkswagen brand in OEM_BRANDS.)
 */
const EXTRA_ALIASES: Record<string, OEM> = {
  gm: 'General Motors'
};

/**
 * Every spelling that resolves to an OEM, built once at module load.
 *
 * Brands come from OEM_BRANDS, so Acura resolves to Honda's ladder and Genesis to Hyundai's
 * without a parallel list to maintain. Roster names are applied after brands so a canonical
 * name always wins a collision.
 */
const OEM_LOOKUP: ReadonlyMap<string, OEM> = (() => {
  const lookup = new Map<string, OEM>();

  for (const [oem, brands] of Object.entries(OEM_BRANDS) as [OEM, string[]][]) {
    for (const brand of brands) {
      lookup.set(normalizeOemKey(brand), oem);
    }
  }
  for (const oem of KNOWN_OEMS) {
    lookup.set(normalizeOemKey(oem), oem);
  }
  for (const [alias, oem] of Object.entries(EXTRA_ALIASES)) {
    lookup.set(normalizeOemKey(alias), oem);
  }

  return lookup;
})();

/**
 * Resolves any OEM, brand or pipeline slug to a roster entry, or undefined if unrecognized.
 * Exported so tests can assert the roster covers every spelling that exists in the data.
 */
export function resolveOem(oem: string | undefined): OEM | undefined {
  return oem ? OEM_LOOKUP.get(normalizeOemKey(oem)) : undefined;
}

/** Human-readable tier names, as shown on badges and labels. */
const TIER_LABELS: Record<ZoneSizeTier, string> = {
  small: 'Small',
  medium: 'Medium',
  large: 'Large',
  'extra-large': 'Extra Large'
};

/** Compact tier names for the map polygon chip, where space is tight. */
const TIER_SHORT_LABELS: Record<ZoneSizeTier, string> = {
  small: 'S',
  medium: 'M',
  large: 'L',
  'extra-large': 'XL'
};

export function getZoneSizeThresholds(oem: string | undefined): ZoneSizeThresholds {
  const resolved = resolveOem(oem);
  return resolved ? OEM_SCALE[resolved] : DEFAULT_SCALE;
}

export function getZoneSizeTier(recallCount: number, oem: string | undefined): ZoneSizeTier {
  const thresholds = getZoneSizeThresholds(oem);
  if (recallCount >= thresholds.extraLarge) return 'extra-large';
  if (recallCount >= thresholds.large) return 'large';
  if (recallCount >= thresholds.medium) return 'medium';
  return 'small';
}

/**
 * Collapses the tier to what `audience` is allowed to see. Customer-facing surfaces stop at
 * Large; internal ones get the tier unchanged.
 */
export function getDisplayTier(tier: ZoneSizeTier, audience: ZoneSizeAudience): ZoneSizeTier {
  return audience === 'customer' && tier === 'extra-large' ? 'large' : tier;
}

export function getZoneSizeTierLabel(
  tier: ZoneSizeTier,
  audience: ZoneSizeAudience = 'internal'
): string {
  return TIER_LABELS[getDisplayTier(tier, audience)];
}

export function getZoneSizeTierShortLabel(
  tier: ZoneSizeTier,
  audience: ZoneSizeAudience = 'internal'
): string {
  return TIER_SHORT_LABELS[getDisplayTier(tier, audience)];
}

/**
 * Drives every "Zone Too Large" advisory. A zone in the top tier is one the admin should
 * consider splitting, which is a different question per OEM.
 */
export function isZoneTooLarge(recallCount: number, oem: string | undefined): boolean {
  return recallCount >= getZoneSizeThresholds(oem).extraLarge;
}

/**
 * Advisory copy, e.g. "Small (0-299), Medium (300-499), or Large (500-699)".
 *
 * Tiers an OEM cannot reach are omitted rather than rendered from the sentinel, which would
 * read "Large (Infinity-Infinity)". A two-rung OEM gets "Small (0-100) or Medium (101+)".
 */
export function describeRecommendedSizes(oem: string | undefined): string {
  const { medium, large, extraLarge } = getZoneSizeThresholds(oem);
  const smallRange = `Small (0-${medium - 1})`;

  if (!Number.isFinite(large)) {
    return `${smallRange} or Medium (${medium}+)`;
  }

  const mediumRange = `Medium (${medium}-${large - 1})`;
  const largeRange = Number.isFinite(extraLarge)
    ? `Large (${large}-${extraLarge - 1})`
    : `Large (${large}+)`;
  return `${smallRange}, ${mediumRange}, or ${largeRange}`;
}
